import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Clock, Shield, CheckCircle2, MapPin } from "lucide-react";
import { EN_CITIES } from "../data/en-cities";
import PriceCalculatorHero from "../components/PriceCalculatorHero";
import { useLang } from "../lib/i18n";

/**
 * English city landing page targeting international customers in Denmark.
 * Path: /en/courier-{slug}
 * Forces language to EN on mount so the navbar/footer also display English.
 */
export default function EnCityLandingPage({ citySlug }) {
  const city = EN_CITIES[citySlug];
  const { setLang } = useLang();

  React.useEffect(() => {
    setLang("en");
    if (!city) return;
    // Update head tags for SEO
    document.title = `Courier in ${city.name} · Same-Day Direct Delivery · EMA Kurer ApS`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute(
      "content",
      `${city.tagline} From 199 DKK incl. 8 km. English-speaking service. Book online.`
    );
    // Canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `https://emakurer.dk/en/courier-${city.slug}`);
    // hreflang alternates (DA + EN + x-default)
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const altDa = document.createElement("link");
    altDa.setAttribute("rel", "alternate");
    altDa.setAttribute("hreflang", "da");
    altDa.setAttribute("href", `https://emakurer.dk/kurer-${city.daSlug}`);
    document.head.appendChild(altDa);
    const altEn = document.createElement("link");
    altEn.setAttribute("rel", "alternate");
    altEn.setAttribute("hreflang", "en");
    altEn.setAttribute("href", `https://emakurer.dk/en/courier-${city.slug}`);
    document.head.appendChild(altEn);
    const altDefault = document.createElement("link");
    altDefault.setAttribute("rel", "alternate");
    altDefault.setAttribute("hreflang", "x-default");
    altDefault.setAttribute("href", `https://emakurer.dk/kurer-${city.daSlug}`);
    document.head.appendChild(altDefault);
  }, [citySlug, city, setLang]);

  if (!city) {
    return (
      <div className="max-w-3xl mx-auto py-32 px-5 text-center">
        <h1 className="font-display text-3xl">City not found</h1>
        <Link to="/" className="btn-primary mt-6 inline-flex">← Back home</Link>
      </div>
    );
  }

  // JSON-LD: FAQPage + Service
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: city.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Courier service",
    provider: {
      "@type": "LocalBusiness",
      name: "EMA Kurer ApS",
      url: "https://emakurer.dk",
      telephone: "+45 70 60 50 40",
      email: "kontakt@emakurer.dk",
      address: { "@type": "PostalAddress", addressCountry: "DK", addressRegion: "Zealand" },
      areaServed: { "@type": "City", name: city.name },
      knowsLanguage: ["da", "en"],
    },
    areaServed: { "@type": "City", name: city.name, addressCountry: "DK" },
    offers: {
      "@type": "Offer",
      priceCurrency: "DKK",
      price: "199",
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "DKK",
        price: "199",
        description: "Same-Day Courier from 199 DKK including 8 km. Extra km: 6 DKK/km.",
      },
    },
  };

  return (
    <div data-testid={`en-city-page-${city.slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }} />

      {/* HERO */}
      <section className="bg-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-12 lg:pt-24">
          <div className="kicker text-brand-red">Premium courier · {city.name} · Denmark</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.05]">
            {city.headline}
          </h1>
          <p className="text-white/75 mt-5 text-lg max-w-3xl leading-relaxed">{city.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/book" className="btn-primary text-base" data-testid={`en-${city.slug}-cta-book`}>
              Book delivery <ArrowRight size={18} />
            </Link>
            <Link to={`/kurer-${city.daSlug}`} className="btn-ghost-light" data-testid={`en-${city.slug}-cta-da`}>
              Læs på dansk →
            </Link>
          </div>
        </div>

        {/* Price calculator embedded directly in hero */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-16">
          <div className="bg-white p-6 sm:p-8 shadow-[8px_8px_0_rgba(220,38,38,0.95)]">
            <div className="kicker mb-3 text-brand-text2">Calculate price instantly</div>
            <PriceCalculatorHero />
          </div>
        </div>
      </section>

      {/* INTRO + Use cases */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-3xl font-bold text-brand-dark">Why international customers choose EMA Kurer in {city.name}</h2>
          <p className="text-brand-text2 mt-5 text-lg leading-relaxed">{city.intro}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {city.useCases.map((u, i) => (
              <div key={i} className="bg-white border border-brand p-5">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={16} className="text-brand-red" />
                  <span className="font-semibold text-brand-dark">{u.t}</span>
                </div>
                <p className="text-sm text-brand-text2 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 self-start space-y-4">
          <div className="bg-brand-dark text-white p-6">
            <div className="kicker text-brand-red">Service types in {city.name}</div>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3"><Clock size={16} className="text-brand-red shrink-0" /><span>Same-Day · from 199 DKK</span></li>
              <li className="flex items-center gap-3"><Truck size={16} className="text-brand-red shrink-0" /><span>Express · from 289 DKK</span></li>
              <li className="flex items-center gap-3"><Shield size={16} className="text-brand-red shrink-0" /><span>Evening · from 249 DKK</span></li>
              <li className="flex items-center gap-3"><CheckCircle2 size={16} className="text-brand-red shrink-0" /><span>Business · monthly invoicing</span></li>
            </ul>
            <Link to="/book" className="btn-primary mt-5 w-full justify-center text-sm">Book now <ArrowRight size={14} /></Link>
          </div>
          <div className="bg-white border border-brand p-6">
            <div className="kicker text-brand-text2 mb-3">Postal codes we cover</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {city.deliveryZones.map((z) => (
                <span key={z} className="bg-brand-bg px-2 py-1 border border-brand">{z}</span>
              ))}
            </div>
          </div>
        </aside>
      </section>

      {/* FAQ */}
      <section className="bg-brand-bg py-14">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Frequently asked</div>
          <h2 className="font-display text-3xl font-bold text-brand-dark mt-2">FAQ — Courier in {city.name}</h2>
          <div className="mt-8 space-y-3">
            {city.faq.map((f, i) => (
              <details key={i} className="bg-white border border-brand p-5 group" data-testid={`en-${city.slug}-faq-${i}`}>
                <summary className="font-semibold text-brand-dark cursor-pointer flex items-center justify-between">
                  {f.q}
                  <span className="text-brand-red text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-brand-text2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other EN cities */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14">
        <div className="kicker text-brand-text2">Also in</div>
        <div className="mt-3 flex flex-wrap gap-3">
          {Object.values(EN_CITIES).filter((c) => c.slug !== city.slug).map((c) => (
            <Link
              key={c.slug}
              to={`/en/courier-${c.slug}`}
              className="border border-brand bg-white px-4 py-2 text-sm font-semibold text-brand-dark hover:bg-brand-dark hover:text-white transition"
              data-testid={`en-other-${c.slug}`}
            >
              <MapPin size={12} className="inline mr-1" /> Courier in {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-dark text-white py-14">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Ready to book your {city.name} courier?</h2>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto">English-speaking service · Transparent pricing · Direct A → B delivery · Live tracking · From 199 DKK.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/book" className="btn-primary text-base">Book delivery now <ArrowRight size={18} /></Link>
            <Link to="/track" className="btn-ghost-light">Track package</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
